import React, { useEffect, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faBoxArchive,
  faBuildingColumns,
  faCartPlus,
  faChartPie,
  faCircleQuestion,
  faCircleUser,
  faFileInvoiceDollar,
  faGear,
  faLocationDot,
  faReceipt,
  faTruckFast,
  faTruckMoving,
  faUserGroup,
} from '@fortawesome/free-solid-svg-icons'

import { useMenu } from 'contexts/MenuContext'
import { DcFlexBox } from 'pixel-base'
import { useApi } from 'shared/hooks'
import styled, { keyframes } from 'styled-components'
import { getMenu } from 'utils/api-routes.util'
import logoSmall from '../../assets/fav-small.svg'
import logo from '../../assets/pixelone-plane-text.png'

import { DoubleChevronLeft } from '../Common'

import MenuItem from './_comp/MenuItem'

interface IMenuItemWithChildren {
  name: string
  route: string
  icon?: string
  permissions_required?: string
  children?: IMenuItemWithChildren[]
}

const PROFIT_ANALYTICS_MENU_ITEM: IMenuItemWithChildren = {
  name: 'Profit Analytics Engine',
  route: '/analytics/profit-analytics',
}

const ensureProfitAnalyticsMenuItem = (
  items: IMenuItemWithChildren[],
): IMenuItemWithChildren[] => {
  return items.map((item) => {
    if (item.route !== '/analytics') {
      return item
    }

    const children = [...(item.children || [])]
    const alreadyPresent = children.some(
      (child) => child.route === PROFIT_ANALYTICS_MENU_ITEM.route,
    )

    if (!alreadyPresent) {
      children.push({ ...PROFIT_ANALYTICS_MENU_ITEM })
    }

    return {
      ...item,
      children,
    }
  })
}

library.add(
  faCartPlus,
  faReceipt,
  faBoxArchive,
  faCircleUser,
  faCircleQuestion,
  faTruckFast,
  faUserGroup,
  faBuildingColumns,
  faChartPie,
  faGear,
  faTruckMoving,
  faLocationDot,
  faFileInvoiceDollar,
)

export interface PixelMenuProps {
  route?: string
  isVisible: boolean
  onClose: () => void
}

const PixelMenu: React.FC<PixelMenuProps> = ({ isVisible, onClose, route }) => {
  const [menu, setMenu] = useState<IMenuItemWithChildren[]>([])
  const [activeRoute, setActiveRoute] = useState('/orders')

  const { isCollapsed, isHovered, setCollapsed, setHovered } = useMenu()

  const { loading: menuLoading } = useApi<{ data: IMenuItemWithChildren[] }>(
    getMenu,
    {
      onSuccess: (data) => {
        const menu = data.data || []

        const newMenu = menu?.map((item) => {
          if (item.route === '/admin/general') {
            item.route = '/admin'
            if (item.children && item.children[0]) {
              item.children[0].route = '/admin'
            }
          }
          return item
        })

        if (data.data) {
          setMenu(ensureProfitAnalyticsMenuItem(newMenu || []))
        }
      },
      skip: !localStorage.getItem('user_info'),
    },
  )

  useEffect(() => {
    setActiveRoute(route || '/orders')
  }, [route])

  const toggleCollapse = () => {
    if (isMobile) {
      if (onClose) {
        onClose()
      }
    } else {
      setCollapsed(!isCollapsed)
    }
  }

  const renderToggleIcon = () => {
    if (isMobile || isHovered) {
      return null
    }
    return (
      <div
        style={{
          transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
          transition: 'transform 0.2s ease',
        }}
      >
        <DoubleChevronLeft onClick={toggleCollapse} />
      </div>
    )
  }

  return (
    <Sidebar
      onMouseEnter={() => setHovered(false)}
      onMouseLeave={() => setHovered(true)}
      isCollapsed={isCollapsed}
      isHovered={isHovered}
      className={isMobile && isVisible ? 'mobile-visible' : ''}
    >
      <DcFlexBox
        height="auto"
        style={{
          justifyContent: 'flex-start',
          alignItems: 'center',
          height: 'auto !important',
          width: isCollapsed && isHovered ? '70%' : '85%',
          background: '#090127',
          border: '1px solid rgb(85 84 92)',
          margin: isCollapsed ? '20px 10px' : '20px',
          borderRadius: '5px',
          padding: '3px 3px',
        }}
      >
        {isCollapsed && isHovered ? (
          <img src={logoSmall} style={{ width: '45px' }} />
        ) : (
          <>
            <img src={logoSmall} style={{ width: '45px' }} />
            <img src={logo} style={{ width: '153px' }} />
          </>
        )}
        {renderToggleIcon()}
      </DcFlexBox>
      <MenuList>
        {menuLoading && (!menu || menu.length === 0) ? (
          <>
            {Array.from({ length: 18 }).map((_, index) => (
              <LoadingTab key={index} />
            ))}
          </>
        ) : (
          menu.map((item) => (
            <MenuItem
              key={item.route}
              activeMenu={activeRoute}
              menu={item}
              setActiveRoute={setActiveRoute}
            />
          ))
        )}
      </MenuList>
    </Sidebar>
  )
}

const Sidebar = styled.div<{ isCollapsed: boolean; isHovered: boolean }>`
  background: #090127;
  width: ${(props) => (props.isCollapsed ? '4.625rem' : '280px')};
  position: ${(props) => (props.isCollapsed ? 'absolute' : 'relative')};
  transition: width 0.3s ease-in-out;
  height: 100vh;
  overflow: hidden;
  z-index: 12;

  &:hover {
    width: 280px;
    transition: width 0.4s ease-in-out;
  }

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    width: 100% !important;
    height: 100vh;
    z-index: 1000;
    transform: translateX(-70%);
    transition: transform 0.3s ease-in-out;

    &.mobile-visible {
      transform: translateX(0);
    }
  }
`

const shimmerGradient = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`

const MenuList = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
  overflow-y: auto;
  height: calc(100vh - 88px);
  &::-webkit-scrollbar {
    width: 5px;
    opacity: 0;
  }

  &:hover {
    &::-webkit-scrollbar {
      opacity: 1;
    }
    &::-webkit-scrollbar-thumb {
      background: #17171720;
    }
  }
`

// Animated gradient tabs shown while menu-new API is in progress
const LoadingTab = styled.li`
  margin: 4px 1.25rem;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(90deg, #1c1233, #2c205b, #22163e);
  background-size: 200% 100%;
  animation: ${shimmerGradient} 1.4s ease-in-out infinite;
  opacity: 0.85;
`

export default PixelMenu
